import { Backdrop, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../../StyledComponents/Container";
import CommonComponet from "./CommonCompoent";
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate } from "react-router-dom";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { useEffect, useState } from "react";
import { deleteAutoDebitDetailsById, getAutoDebitDetailsById } from "../../services/AutoDebit/AutoDebitServices";
import * as constants from "../../../../constants";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import basestyle from "../../../CSS/Base.module.css";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import { useTranslation } from "react-i18next";
import DeletePopUp from "../../../PopUp/PopUp";
import { notifyUser } from "../../Utils/notifyUser";

const formatDatetimeStampToDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
};

interface Column {
    id: string;
    label: string;
    minWidth: number;
    align?: "center" | "left" | "right";
}

const AutoDebitScheduleRule = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const rowData = location.state
    const autoDebitId = rowData.autoDebitId;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [detailsData, setDetailsData] = useState<any>({})
    const [historyData, setHistoryData] = useState([])
    const [blackdropFlag, setBlackdropFlag] = useState(false)
    const [openPopUp, setOpenPopUp] = useState(false);
    const restrictedRoles = [
        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
        constants.ROLES_READ_ONLY_USER,
        constants.ROLES_PROJECT_APPROVER,
        constants.ROLES_FINANCIAL_ANALYST
    ];
    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleEditClick = () => {
        navigate("/autoDebitLoanDetails", { state: { detailsData: detailsData } });
    }

    const handleDelete = () => {
        setOpenPopUp(true);
    };
    const handlePopUpClose = () => {
        setOpenPopUp(false);
    };
    function handleConfirm() {
        deleteDetailsByAutoDebitLoanId(autoDebitId, userInfo.clientId);
    }

    const formData = {
        loanNumber: detailsData.loanNumber,
        source: detailsData.entityType,
        description: detailsData.loanDescription,
        isRecurring: detailsData.paymentRecurring == "Yes" ? true : false,
        frequency: detailsData.frequency,
        endDate: detailsData.paymentEndingDate,
        amount: detailsData.amount,
        autoDebit: detailsData.autoDebitOption,
        beginningDate: formatDate(detailsData.paymentBeginningDate),
        fundId: detailsData.entityId,
        fundType: detailsData.entityType
    };

    async function getDetailsByAutoDebitId(Id: number, clientId: number) {
        setBlackdropFlag(true)
        try {
            const response = await getAutoDebitDetailsById(Id, clientId);

            if (response.status === constants.STATUS_CODES.SUCCESS) {
                const data = response.data.data[0];
                const data1 = data.autoDebitScheduleHistory.map((data: any) => ({
                    scheduleRunDate: formatDatetimeStampToDate(data.executionDate),
                    description: data.errorMessage,
                    status: data.status
                }))
                setDetailsData(data);
                setHistoryData(data1)
            } else {
                notifyUser(response.response.data.message, 'error', translate("autoDebitScheduleRule"));
            }
        } catch (error) {
            console.error("Error handling fund source:", error);
        } finally {
            setBlackdropFlag(false);
        }
    }

    async function deleteDetailsByAutoDebitLoanId(Id: number, clientId: number) {
        try {
            const response = await deleteAutoDebitDetailsById(Id, clientId);

            if (response.status === constants.STATUS_CODES.SUCCESS) {
                const message = response.data.message
                navigate("/autoDebitListing", { state: { message: message } });
                setOpenPopUp(false);
            } else {
                notifyUser(response.response.data.message, 'error', translate("autoDebitScheduleRule"));
                setOpenPopUp(false);
            }
        } catch (error) {
            console.error("Error handling fund source:", error);
        }
    }

    useEffect(() => {
        if (autoDebitId) {
            getDetailsByAutoDebitId(autoDebitId, userInfo.clientId)
        }
    }, [])

    const Columns: Column[] = [
        { id: "scheduleRunDate", label: translate('scheduleRunDate'), minWidth: 50, align: "center" },
        { id: "description", label: translate('description'), minWidth: 450, align: "left" },
        { id: "status", label: translate('status'), minWidth: 50, align: "center" },
    ];
    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>
                        {translate('autoDebitScheduleRule')} - #{autoDebitId}&nbsp;
                        <IconButton
                            onClick={handleEditClick}
                            disabled={detailsData.status === "Completed" || restrictedRoles.includes(userInfo?.roles?.roleName ?? '')}
                            sx={{ padding: 0 }}
                            aria-label="edit"
                        >
                            <EditIcon sx={{ cursor: detailsData.status === "Completed" ? 'default' : 'pointer' }} />
                        </IconButton>
                    </SectionTitle>
                    <Box sx={{ width: "100%" }}>
                        <CommonComponet data={formData} />
                        {historyData.length > 0 && <Box sx={{ mt: 2 }}>
                            <SubSectionTitle>{translate('scheduleHistory')}</SubSectionTitle>
                            <Box sx={{ px: 1 }}>
                                <ReusableTable
                                    columns={Columns}
                                    rows={historyData}
                                    count={4}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </Box>
                        </Box>}
                        <Box className={basestyle.buttoncontainer} >
                            <ContainedButton style={{ backgroundColor: (detailsData.status === "Completed" || restrictedRoles.includes(userInfo?.roles?.roleName ?? '')) ? '#b5b8b9' : '#e46962' }} onClick={handleDelete} disabled={detailsData.status === "Completed" || restrictedRoles.includes(userInfo?.roles?.roleName ?? '')}> {translate('deleteRule')} </ContainedButton>
                            <ContainedButton onClick={() => navigate("/autoDebitListing")}> {translate('back')}  </ContainedButton>
                        </Box>
                        <DeletePopUp
                            open={openPopUp}
                            popupTitle={translate('confirmDelete')}
                            handleClose={handlePopUpClose}
                            saveChanges={() => handleConfirm()}
                            message={translate('doYouWantToDeleteAutoDebitRule')}
                        />
                    </Box>
                    <Backdrop
                        sx={(theme) => ({
                            color: "#fff",
                            zIndex: theme.zIndex.drawer + 1,
                            display: "flex",
                            flexDirection: "column",
                        })}
                        open={blackdropFlag}
                    >
                        <CircularProgress color="inherit" />
                        <Typography color="white">Please wait...</Typography>
                    </Backdrop>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    )
}
export default AutoDebitScheduleRule;